import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  metadata: {
    bookingId?: number;
    videoId?: number;
    actionUrl?: string;
    priority?: 'low' | 'medium' | 'high';
  };
  readAt: string | null;
  read_at?: string | null; // Database field name
  createdAt: string;
  created_at?: string; // Database field name
}

interface NotificationResponse {
  notifications: Notification[];
  total: number;
  page: number;
  totalPages: number;
}

interface NotificationPreference {
  notificationType: string;
  email: boolean;
  sms: boolean;
  inApp: boolean;
  push: boolean;
}

// Fetch notifications with pagination
async function fetchNotifications({ 
  pageParam = 1, 
  filter 
}: { 
  pageParam?: number; 
  filter: 'all' | 'unread' 
}): Promise<NotificationResponse> {
  const response = await fetch(
    `/api/notifications?page=${pageParam}&limit=20&filter=${filter}`,
    { credentials: 'include' }
  );
  
  if (!response.ok) {
    throw new Error('Failed to fetch notifications');
  }
  
  return response.json();
}

// Fetch unread count
async function fetchUnreadCount(): Promise<{ count: number }> {
  const response = await fetch('/api/notifications/unread-count', {
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch unread count');
  }
  
  return response.json();
}

// Mark notification as read
async function markNotificationAsRead(id: number): Promise<void> {
  const response = await fetch(`/api/notifications/${id}/read`, {
    method: 'PATCH',
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new Error('Failed to mark notification as read');
  }
}

// Mark all notifications as read
async function markAllNotificationsAsRead(): Promise<void> {
  const response = await fetch('/api/notifications/read-all', {
    method: 'PATCH',
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new Error('Failed to mark all notifications as read');
  }
}

// Fetch notification preferences
async function fetchNotificationPreferences(): Promise<NotificationPreference[]> {
  const response = await fetch('/api/notifications/preferences', {
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch notification preferences');
  }
  
  return response.json();
}

// Update notification preferences
async function updateNotificationPreferences(
  preferences: NotificationPreference[]
): Promise<NotificationPreference[]> {
  const response = await fetch('/api/notifications/preferences', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(preferences),
  });
  
  if (!response.ok) {
    throw new Error('Failed to update notification preferences');
  }
  
  return response.json();
}

// Hook for fetching notifications with infinite scroll
export function useNotifications(filter: 'all' | 'unread' = 'all') {
  return useInfiniteQuery({
    queryKey: ['notifications', filter],
    queryFn: ({ pageParam }) => fetchNotifications({ pageParam, filter }),
    getNextPageParam: (lastPage) => 
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    refetchInterval: 30000, // Poll every 30 seconds
    staleTime: 0, // Always consider data stale to ensure polling works
    initialPageParam: 1,
  });
}

// Hook for fetching unread count
export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: fetchUnreadCount,
    refetchInterval: 30000, // Poll every 30 seconds
    staleTime: 0, // Always consider data stale to ensure polling works
  });
}

// Hook for marking notification as read
export function useMarkAsRead() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: markNotificationAsRead,
    onSuccess: () => {
      // Invalidate all notification queries
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// Hook for marking all notifications as read
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: markAllNotificationsAsRead,
    onSuccess: () => {
      // Invalidate all notification queries
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// Hook for fetching notification preferences
export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['notification-preferences'],
    queryFn: fetchNotificationPreferences,
    staleTime: Infinity, // Preferences don't change often
  });
}

// Hook for updating notification preferences
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: updateNotificationPreferences,
    onSuccess: (data) => {
      queryClient.setQueryData(['notification-preferences'], data);
    },
  });
}